import type { PermissionKey } from '@kg-internal/pv3-helpers';
import portalconfig from '../../config';
import {
  type clientPermissionLevel,
  hasAtLeastLimitedClientAccess,
  hasFullClientAccess,
  hasPermissions,
} from './permissionChecks';

export type { clientPermissionLevel };

export interface IUserObj {
  id: number;
  details: {
    id: number;
    email: string;
    nameFirst: string;
    nameLast: string;
    jobTitle?: string | null;
    hasHandbookUpdates: boolean;
    hasTimesheetDue: boolean;
    photoUrl: string;
    isPartTime: boolean;
    isManager: boolean;
    manager?: {
      id: number;
      nameFirst: string;
      nameLast: string;
      email: string;
    } | null;
    tracksPTO: boolean;
    office?: {
      OfficeID: number;
      Name: string;
    } | null;
    department?: {
      DepartmentID: number;
      Name: string;
      IsDirectoryRestricted?: boolean;
    } | null;
    /**
     * The permission keys this person holds, in the NEW vocabulary and correct in both flag positions
     * — `Person.permissionKeys`. The legacy `permissions` list it replaced is on its way out with
     * `Person.permissions()`; nothing in the browser reads a permission NAME any more.
     */
    permissionKeys: PermissionKey[];
    accountServicesTeams: {
      id: number;
      permissionType: clientPermissionLevel;
      projectAccountID: number;
    }[];
    featureFlagListV2: {
      name: string;
      active: boolean;
    }[];
  } | null;
  env: string;
  config: typeof portalconfig;
  app: string | null;
  version: string | null;
  isDepartment: (departmentIds: number | number[]) => boolean;
  /**
   * Keys, not legacy names. Typed as the declared union so a name that is not a permission — or one
   * that has not been converted yet — is a build error rather than a check that silently never fires.
   */
  hasPermissions: (permissionKeys: PermissionKey | readonly PermissionKey[], allRequired?: boolean) => boolean;
  /**
   * The two client-access questions, one method each, mirroring `clientPermissionGate.ts` on the server.
   * There is no levels array and nothing to omit — see ./permissionChecks for what that closed.
   */
  hasFullClientAccess: (accountId: number) => boolean;
  hasAtLeastLimitedClientAccess: (accountId: number) => boolean;
  hasFlags: (flagNames: string | string[], allRequired?: boolean) => boolean;
  /**
   * Whether this person sees only themselves in the directory. A property of their department, not a
   * permission they hold — it replaced the legacy `directory.restricted` key, and the server decides
   * it in one place (`User.build`) and hands the answer over on `me`.
   */
  isDirectoryRestricted: boolean;
  /**
   * True while this user is the cached `portalMe` profile and the live one is still loading. A refusal
   * drawn from it is not an answer yet: a page shows its loader, never the no-access page, until it is false.
   */
  isProvisional: boolean;
}

const userObj = (
  usr,
  env: string,
  config: typeof portalconfig,
  name: string | null,
  version: string | null,
  isProvisional = false,
): IUserObj => {
  return {
    id: usr.me.id,
    details: {
      ...usr.me,
      featureFlagListV2: usr.featureFlagListV2 || [],
    },
    env: env,
    config: config,
    app: name,
    version: version,
    // The server's answer, not a second copy of the rule: `User.build` calls itself THE ONE PLACE the
    // restriction is decided and this used to mirror it here, department flag and `directory.manager`
    // exemption included.
    isDirectoryRestricted: !!usr.me.isDirectoryRestricted,
    isProvisional,
    isDepartment: (departmentIds) => {
      if (Array.isArray(departmentIds)) {
        return departmentIds.some((s) => usr.me.department.DepartmentID === s);
      } else {
        return usr.me.department.DepartmentID === departmentIds;
      }

      return false;
    },
    // BOTH DECISIONS LIVE IN ./permissionChecks, which imports no app config and so can be tested from
    // server/graph — packages/uikit has no test runner. Delegating rather than restating them is what
    // keeps "an empty requirement refuses" one statement instead of two that can drift.
    // `?? []` so an absent set refuses rather than throws. PortalContext ignores a cached `portalMe` blob
    // of an older shape, so a set is absent only if the server omitted it.
    hasPermissions: (permissionKeys, allRequired = false) =>
      hasPermissions(usr.me.permissionKeys ?? [], permissionKeys, allRequired),
    hasFullClientAccess: (accountId) =>
      hasFullClientAccess(usr.me.permissionKeys ?? [], usr.me.accountServicesTeams, accountId),
    hasAtLeastLimitedClientAccess: (accountId) =>
      hasAtLeastLimitedClientAccess(usr.me.permissionKeys ?? [], usr.me.accountServicesTeams, accountId),
    hasFlags: (flagNames, allRequired = false) => {
      if (Array.isArray(flagNames)) {
        if (allRequired) {
          return flagNames.every((flag) => (usr.featureFlagListV2 || []).some((ff) => ff.name === flag && ff.active));
        } else {
          return flagNames.some((flag) => (usr.featureFlagListV2 || []).some((ff) => ff.name === flag && ff.active));
        }
      } else {
        return (usr.featureFlagListV2 || []).some((flag) => flag.name === flagNames && flag.active);
      }
      return false;
    },
  };
};

export default userObj;
